import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CoverImg } from "@/components/ui/CoverImg";
import { TrackList } from "@/components/tracks/TrackList";
import { musicService } from "@/services/musicService";
import { usePlayer } from "@/hooks/usePlayer";
import { useCoverColor } from "@/hooks/useCoverColor";
import { useStickyHeaderTrigger } from "@/hooks/useStickyHeaderTrigger";
import { usePreloadImage } from "@/hooks/usePreloadImage";
import { coverUrl } from "@/lib/cover";
import { fmtDuration } from "@/utils/format";
import type { Playlist, Track } from "@/types/types";

export function PlaylistPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState<Playlist | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [suggestions, setSuggestions] = useState<Track[]>([]);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const player = usePlayer();
  const wash = useCoverColor(p?.cover_id);
  const { sentinelRef, visible: stickyVisible } = useStickyHeaderTrigger();
  usePreloadImage(p?.cover_id ? coverUrl(p.cover_id, 232) : null);

  const loadTracks = useCallback(() => {
    musicService.playlistTracks(id).then(setTracks).catch(console.error);
  }, [id]);

  useEffect(() => {
    musicService.playlist(id).then(setP).catch(console.error);
    loadTracks();
    musicService.topTracks("month", 20)
      .then((rows) => setSuggestions(rows.map((r) => r.track)))
      .catch(console.error);
    setJustAdded(new Set());
  }, [id, loadTracks]);

  const existing = useMemo(() => new Set(tracks.map((t) => t.id)), [tracks]);
  const visibleSuggestions = useMemo(
    () => suggestions.filter((t) => !existing.has(t.id)).slice(0, 12),
    [suggestions, existing],
  );

  const quickAdd = useCallback(async (t: Track) => {
    setAddingId(t.id);
    try {
      await musicService.addTracksToPlaylist(id, [t.id]);
      setJustAdded((s) => { const n = new Set(s); n.add(t.id); return n; });
      loadTracks();
    } catch (e) { console.error(e); }
    finally { setAddingId(null); }
  }, [id, loadTracks]);

  const deletePlaylist = useCallback(async () => {
    if (!p) return;
    if (!window.confirm(`Supprimer "${p.name}" ? C'est définitif.`)) return;
    try {
      await musicService.deletePlaylist(id);
      nav("/library");
    } catch {
      window.alert("Suppression impossible.");
    }
  }, [id, p, nav]);

  if (!p) return null;
  return (
    <div className="page-hero-wrap">
      <div className="hero-wash" style={{
        background: wash
          ? `linear-gradient(180deg, ${wash} 0%, transparent 65%)`
          : "transparent",
      }} />

      <div className={"page-sticky-header" + (stickyVisible ? " visible" : "")}>
        <CoverImg coverId={p.cover_id} alt={p.name} size={36} />
        <strong className="trunc">{p.name}</strong>
        <div className="grow" />
        <button className="btn primary" style={{ padding: "8px 14px" }}
                onClick={() => void player.play(tracks, 0)}>▶</button>
      </div>

      <header className="hero" style={{
        display: "flex", flexDirection: "row", alignItems: "flex-end",
        gap: 28, marginBottom: 28,
      }}>
        <CoverImg coverId={p.cover_id} alt={p.name} size={232} priority />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6, minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 13 }}>Playlist</div>
          <h1 style={{ margin: 0, fontSize: 52, lineHeight: 1.05, fontWeight: 800 }}>{p.name}</h1>
          {p.description && <div className="muted" style={{ fontSize: 14 }}>{p.description}</div>}
          <div className="row" style={{ marginTop: 10, gap: 8 }}>
            <button className="btn primary"
                    onClick={() => void player.play(tracks, 0)}>▶ Lire</button>
            <button className="btn" onClick={() => void deletePlaylist()}>Supprimer</button>
          </div>
        </div>
      </header>
      <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

      <TrackList tracks={tracks} />

      {visibleSuggestions.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
            <h2 style={{ margin: 0 }}>Suggestions</h2>
            <span className="muted" style={{ fontSize: 13 }}>Basé sur ce que t'écoutes en ce moment</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginTop: 12 }}>
            {visibleSuggestions.map((t) => {
              const added = justAdded.has(t.id);
              const busy = addingId === t.id;
              return (
                <div key={t.id} className="row" style={{ padding: "8px 10px", borderRadius: 8, gap: 12,
                                                          alignItems: "center" }}>
                  <div style={{ width: 44, height: 44 }}>
                    <CoverImg coverId={t.album?.cover_id} alt={t.title} size={44} />
                  </div>
                  <button className="grow" style={{ textAlign: "left" }}
                          onClick={() => void player.playOne(t)}>
                    <div className="trunc">{t.title}</div>
                    <div className="muted trunc">{t.artist.name}</div>
                  </button>
                  <span className="muted" style={{ minWidth: 46, textAlign: "right" }}>
                    {fmtDuration(t.duration_ms)}
                  </span>
                  <button
                    title={added ? "Ajouté à la playlist" : "Ajouter à cette playlist"}
                    onClick={() => void quickAdd(t)}
                    disabled={added || busy}
                    style={{
                      fontSize: 18, lineHeight: 1, padding: "4px 12px", borderRadius: 999,
                      border: "1px solid var(--line)",
                      background: added ? "var(--accent)" : "transparent",
                      color: added ? "#000" : "var(--fg)",
                      cursor: added ? "default" : "pointer",
                      minWidth: 40,
                    }}
                  >
                    {busy ? "…" : added ? "✓" : "+"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
